import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import {
  API_GET_TECHSTACKS_LIST,
  EMPLOYEE_LIST,
  GET_DEPARTMENT_LIST,
  GET_DESIGNATION_LIST,
  API_USER_SHIFTS,
} from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import type { SelectOption } from "../../../ui/select/select.tsx";
import { toCompanyNameOptions } from "../../../../hooks/use-company-name-options.ts";
import { ApiError, getApi } from "../../../../utils/api.ts";

const idNameRowSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
});

const managerRowSchema = z.object({
  id: z.union([z.number(), z.string()]),
  empName: z.string(),
  employeeType: z.string().optional(),
});

const techStackRowSchema = z.object({
  name: z.string(),
  type: z.string(),
});

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

function unwrapDataArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === "object") {
    const d = (raw as Record<string, unknown>).data;
    if (Array.isArray(d)) return d;
  }
  return [];
}

function toIdNameOptions(items: unknown[]): SelectOption[] {
  const parsed = z.array(idNameRowSchema).safeParse(items);
  if (!parsed.success) return [];
  return parsed.data.map((r) => ({
    value: String(r.id),
    label: r.name,
  }));
}

function toManagerOptions(items: unknown[]): SelectOption[] {
  const parsed = z.array(managerRowSchema).safeParse(items);
  if (!parsed.success) return [];
  return parsed.data.map((r) => {
    const type = r.employeeType ?? "";
    return {
      value: String(r.id),
      label: type ? `${r.empName} (${type})` : r.empName,
    };
  });
}

function toTechStackOptions(items: unknown[]): SelectOption[] {
  const parsed = z.array(techStackRowSchema).safeParse(items);
  if (!parsed.success) return [];

  return parsed.data
    .filter((row) => row.name.trim().toUpperCase() !== "OTHERS")
    .map((row) => ({ value: row.name, label: row.type }));
}

export function useHrSetupLookups() {
  const [departments, setDepartments] = useState<SelectOption[]>([]);
  const [managers, setManagers] = useState<SelectOption[]>([]);
  const [techStacks, setTechStacks] = useState<SelectOption[]>([]);
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [designations, setDesignations] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [designationsLoading, setDesignationsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBase = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const token = accessToken();
      const [deptRes, mgrRes, techRes, companyRes] = await Promise.all([
        getApi<unknown>(GET_DEPARTMENT_LIST, token, "json", signal),
        getApi<unknown>(EMPLOYEE_LIST, token, "json", signal),
        getApi<unknown>(API_GET_TECHSTACKS_LIST, token, "json", signal),
        getApi<unknown>(API_USER_SHIFTS, token, "json", signal),
      ]);
      setDepartments(toIdNameOptions(unwrapDataArray(deptRes.data)));
      setManagers(toManagerOptions(unwrapDataArray(mgrRes.data)));
      setTechStacks(toTechStackOptions(unwrapDataArray(techRes.data)));
      setCompanies(toCompanyNameOptions(companyRes.data));
    } catch (e) {
      if (signal?.aborted) return;
      const msg = e instanceof ApiError ? e.message : "Failed to load HR setup lookups.";
      setError(msg);
      setDepartments([]);
      setManagers([]);
      setTechStacks([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDesignations = useCallback(async (departmentId: string, signal?: AbortSignal) => {
    if (departmentId.trim() === "") {
      setDesignations([]);
      return;
    }
    setDesignationsLoading(true);
    try {
      const { data } = await getApi<unknown>(
        `${GET_DESIGNATION_LIST}?departmentId=${encodeURIComponent(departmentId)}`,
        accessToken(),
        "json",
        signal,
      );
      setDesignations(toIdNameOptions(unwrapDataArray(data)));
    } catch {
      setDesignations([]);
    } finally {
      setDesignationsLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void loadBase(ac.signal);
    return () => ac.abort();
  }, [loadBase]);

  return {
    departments,
    managers,
    techStacks,
    companies,
    designations,
    loading,
    designationsLoading,
    error,
    refetch: loadBase,
    fetchDesignations,
  };
}
